/**
 * PolaroidCatalog — generic list + detail view for the 3 Polaroid categories:
 * gifts, market, lessons. Layout is Instagram-style on desktop: image gallery
 * on the left, content on the right. On mobile: stacked.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { COMMUNITY_BRANDS } from './communityBrands';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const META = {
  gifts: {
    emoji: '🎁',
    title: { ca: 'Regals i Premis', es: 'Regalos y Premios', en: 'Gifts & Prizes', fr: 'Cadeaux & Prix' },
    subtitle: { ca: 'Sorpreses només pels membres', es: 'Sorpresas solo para socios', en: 'Surprises just for members', fr: 'Surprises pour les membres' },
    accent: '#F4D77A',
  },
  market: {
    emoji: '🛒',
    title: { ca: 'Mercat', es: 'Mercado', en: 'Marketplace', fr: 'Marché' },
    subtitle: { ca: 'Nous i 2a mà · compartit entre comunitats', es: 'Nuevos y 2ª mano · compartido', en: 'New & 2nd hand · shared', fr: 'Neufs & occasion · partagé' },
    accent: '#E8B4B4',
  },
  lessons: {
    emoji: '⛳',
    title: { ca: 'Classes particulars', es: 'Clases particulares', en: 'Private lessons', fr: 'Cours particuliers' },
    subtitle: { ca: 'Un a un, fes una cita', es: 'Uno a uno, pide cita', en: 'One-to-one, book a slot', fr: 'En tête-à-tête' },
    accent: '#B8C5E0',
  },
};

const STATE_LABEL = {
  active: { ca: '', es: '', en: '', fr: '' },
  reserved: { ca: 'RESERVAT', es: 'RESERVADO', en: 'RESERVED', fr: 'RÉSERVÉ' },
  sold: { ca: 'VENUT', es: 'VENDIDO', en: 'SOLD', fr: 'VENDU' },
};

const pickLang = (obj, lang) => {
  if (!obj) return '';
  const code = (lang || 'ca').split('-')[0];
  return obj[code] || obj.ca || obj.es || obj.en || obj.fr || '';
};

const isNew = (created_at) => {
  if (!created_at) return false;
  const d = new Date(created_at);
  return (Date.now() - d.getTime()) < 7 * 24 * 3600 * 1000;
};

// === LIST PAGE ===
export const PolaroidCatalog = ({ community, category }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = META[category];
  const brand = COMMUNITY_BRANDS[community];

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/polaroid/${category}`, { params: { community } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error('No s\'ha pogut carregar el catàleg'))
      .finally(() => setLoading(false));
    // Mark category as seen (clears the unread badge)
    const token = localStorage.getItem(`${community}_token`);
    if (token) {
      axios.post(`${API_URL}/api/polaroid/${category}/mark-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  }, [community, category]);

  return (
    <div className="min-h-screen bg-[#062714] text-[#F2E8D5]" data-testid={`polaroid-catalog-${category}`}>
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16">
        <Link
          to={`/${community}`}
          className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6] hover:text-[#D4AF37]"
          data-testid="polaroid-back"
        >
          ← {brand?.label || community}
        </Link>
        <div className="mt-8">
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">
            {meta.emoji} {pickLang(meta.title, lang)}
          </div>
          <h1 className="mt-2 font-serif text-3xl md:text-5xl tracking-tight">
            {pickLang(meta.title, lang)}
          </h1>
          <p className="mt-2 text-[#A8B4A6] italic">{pickLang(meta.subtitle, lang)}</p>
        </div>

        {loading ? (
          <div className="mt-16 text-center text-[#A8B4A6] tracking-widest">…</div>
        ) : items.length === 0 ? (
          <div className="mt-16 text-center text-[#A8B4A6]">
            <div className="text-5xl mb-3 opacity-60">{meta.emoji}</div>
            <p className="italic">Encara no hi ha res aquí. Aviat publicarem.</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it) => (
              <ItemCard
                key={it.id}
                item={it}
                lang={lang}
                accent={meta.accent}
                onOpen={() => navigate(`/${community}/${categoryToSlug(category)}/${it.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ItemCard = ({ item, lang, accent, onOpen }) => {
  const cover = item.images?.[0];
  const stateLabel = pickLang(STATE_LABEL[item.state] || {}, lang);
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.4 }}
      className="text-left bg-[#FBF7EE] text-[#1a1a1a] rounded-lg overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_40px_rgba(0,0,0,0.45)] transition-shadow"
      data-testid={`polaroid-item-${item.id}`}
    >
      <div className="relative aspect-square bg-gradient-to-br from-[#0E4D2A] to-[#083B1F] flex items-center justify-center overflow-hidden">
        {cover ? (
          <img src={cover} alt={pickLang(item.title, lang)} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <span className="text-5xl opacity-50">🖼️</span>
        )}
        {item.featured && (
          <span
            className="absolute top-3 left-3 text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: accent, color: '#1a1a1a' }}
          >
            ★ Destacat
          </span>
        )}
        {isNew(item.created_at) && (
          <span className="absolute top-3 right-3 bg-[#D4AF37] text-[#062714] text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full">
            NOU
          </span>
        )}
        {stateLabel && (
          <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center py-2 bg-black/55 text-white font-bold tracking-[0.3em] text-sm uppercase rotate-[-8deg]">
            {stateLabel}
          </span>
        )}
        {item.images?.length > 1 && (
          <span className="absolute bottom-3 right-3 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">
            📷 {item.images.length}
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="font-serif text-lg leading-tight">{pickLang(item.title, lang)}</div>
        <div className="mt-1 text-sm text-slate-600 line-clamp-2">{pickLang(item.intro, lang)}</div>
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          {item.price ? (
            <span className="font-bold text-[#0E4D2A]">{item.price.amount} {item.price.currency || 'EUR'}</span>
          ) : <span />}
          {item.likes_count > 0 && <span>👍 {item.likes_count}</span>}
        </div>
      </div>
    </motion.button>
  );
};

const categoryToSlug = (cat) => ({
  gifts: 'regals',
  market: 'mercat',
  lessons: 'classes-particulars',
}[cat] || cat);


// === DETAIL PAGE (Instagram-style) ===
export const PolaroidItemView = ({ community, category }) => {
  const { itemId } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [liked, setLiked] = useState(false);
  const [idx, setIdx] = useState(0);
  const meta = META[category];
  const brand = COMMUNITY_BRANDS[community];

  useEffect(() => {
    axios.get(`${API_URL}/api/polaroid/${category}/${itemId}`).then((r) => setItem(r.data)).catch(() => {
      toast.error('No s\'ha trobat l\'item');
      navigate(`/${community}/${categoryToSlug(category)}`);
    });
    const token = localStorage.getItem(`${community}_token`);
    if (token) {
      axios.get(`${API_URL}/api/polaroid/${category}/${itemId}/like-status`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => setLiked(!!r.data?.liked)).catch(() => {});
    }
  }, [itemId, category, community]);

  const toggleLike = async () => {
    const token = localStorage.getItem(`${community}_token`);
    if (!token) {
      toast.error('Has de ser membre per puntuar');
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/polaroid/${category}/${itemId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLiked(r.data.liked);
      setItem((it) => it ? { ...it, likes_count: r.data.likes_count } : it);
    } catch {
      toast.error('Error');
    }
  };

  if (!item) {
    return <div className="min-h-screen bg-[#062714] text-[#A8B4A6] text-center pt-40">…</div>;
  }

  const images = item.images?.length ? item.images : [null];
  const stateLabel = pickLang(STATE_LABEL[item.state] || {}, lang);
  const customLinks = item.links?.custom || [];

  return (
    <div className="min-h-screen bg-[#062714] text-[#F2E8D5]" data-testid={`polaroid-detail-${itemId}`}>
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16">
        <Link
          to={`/${community}/${categoryToSlug(category)}`}
          className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6] hover:text-[#D4AF37]"
          data-testid="polaroid-detail-back"
        >
          ← {meta.emoji} {pickLang(meta.title, lang)}
        </Link>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* LEFT — gallery */}
          <div>
            <div className="relative aspect-square bg-[#083B1F] rounded-xl overflow-hidden border border-[#D4AF37]/20">
              <AnimatePresence mode="wait">
                <motion.img
                  key={idx}
                  src={images[idx] || ''}
                  alt={pickLang(item.title, lang)}
                  className="w-full h-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                />
              </AnimatePresence>
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white"
                    data-testid="polaroid-gallery-prev"
                  >‹</button>
                  <button
                    type="button"
                    onClick={() => setIdx((i) => (i + 1) % images.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white"
                    data-testid="polaroid-gallery-next"
                  >›</button>
                </>
              )}
              {stateLabel && (
                <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center py-2 bg-black/55 text-white font-bold tracking-[0.3em] text-base uppercase rotate-[-8deg]">
                  {stateLabel}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex justify-center gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-[#D4AF37] w-6' : 'bg-[#A8B4A6]/40'}`}
                    aria-label={`Image ${i+1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — content */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {item.featured && <span className="text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#062714]">★ Destacat</span>}
              {isNew(item.created_at) && <span className="text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-0.5 rounded-full bg-[#A8D5BA] text-[#062714]">NOU</span>}
              {item.subcategory && <span className="text-[10px] uppercase tracking-wider text-[#A8B4A6]">· {item.subcategory}</span>}
            </div>
            <h1 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight leading-tight">
              {pickLang(item.title, lang)}
            </h1>
            {pickLang(item.intro, lang) && (
              <p className="mt-2 text-[#A8B4A6] italic text-lg">{pickLang(item.intro, lang)}</p>
            )}
            {item.price && (
              <div className="mt-4 font-bold text-2xl text-[#D4AF37]">
                {item.price.amount} {item.price.currency || 'EUR'}
              </div>
            )}
            {pickLang(item.body, lang) && (
              <div className="mt-5 text-[#F2E8D5]/90 whitespace-pre-wrap leading-relaxed">
                {pickLang(item.body, lang)}
              </div>
            )}

            {/* Links */}
            {(item.links?.url || item.links?.email || item.links?.whatsapp || customLinks.length > 0) && (
              <div className="mt-6 flex flex-wrap gap-3" data-testid="polaroid-links">
                {item.links?.url && (
                  <a href={item.links.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase">
                    🔗 Web
                  </a>
                )}
                {item.links?.email && (
                  <a href={`mailto:${item.links.email}`}
                    className="inline-flex items-center gap-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase">
                    📧 Email
                  </a>
                )}
                {item.links?.whatsapp && (
                  <a href={`https://wa.me/${item.links.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase">
                    💬 WhatsApp
                  </a>
                )}
                {customLinks.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] px-4 py-2 rounded-full text-xs tracking-[0.18em] uppercase">
                    → {l.label || 'Enllaç'}
                  </a>
                ))}
              </div>
            )}

            {/* Like */}
            <div className="mt-8 flex items-center gap-3">
              <button
                type="button"
                onClick={toggleLike}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${liked ? 'bg-[#D4AF37] text-[#062714]' : 'border border-[#A8B4A6]/40 text-[#A8B4A6] hover:border-[#D4AF37] hover:text-[#D4AF37]'}`}
                data-testid="polaroid-like"
              >
                {liked ? '👍 M\'agrada' : '👍 M\'agrada'}
              </button>
              <span className="text-[#A8B4A6] text-sm" data-testid="polaroid-likes-count">
                {item.likes_count || 0} {item.likes_count === 1 ? 'persona' : 'persones'}
              </span>
            </div>

            <div className="mt-10 text-[10px] tracking-[0.18em] uppercase text-[#A8B4A6]/60">
              Publicat el {new Date(item.created_at).toLocaleDateString(lang)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolaroidCatalog;
